import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { WhatsappLogo, InstagramLogo, MagnifyingGlass, PaperPlaneRight, Sparkle, Tag, User, ArrowsClockwise, Shield, Download, CheckCircle } from "@phosphor-icons/react";

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60_000) return "now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const channelIcon = (ch, size = 14) => {
  if (ch === "whatsapp") return <WhatsappLogo size={size} weight="fill" className="text-[#25D366]" />;
  if (ch === "instagram") return <InstagramLogo size={size} weight="duotone" className="text-[#E4405F]" />;
  return null;
};

export default function ChannelInbox() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState({ channel: "", q: "" });
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const msgEndRef = useRef(null);

  const loadList = async () => {
    setLoadingList(true);
    try {
      const params = [];
      if (filter.channel) params.push(`channel=${filter.channel}`);
      if (filter.q) params.push(`q=${encodeURIComponent(filter.q)}`);
      const { data } = await api.get(`/channel/conversations${params.length ? "?" + params.join("&") : ""}`);
      setConversations(data);
    } catch (e) { console.error(e); }
    finally { setLoadingList(false); }
  };

  const loadDetail = async (conv_id) => {
    try {
      const { data } = await api.get(`/channel/conversations/${conv_id}`);
      setDetail(data);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadList(); }, [filter.channel]);
  useEffect(() => {
    if (selected) loadDetail(selected);
  }, [selected]);

  const send = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    const textToSend = replyText;
    // Optimistic UI — append immediately
    const optimistic = {
      message_id: "tmp_" + Date.now(),
      direction: "outbound",
      channel: detail?.conversation?.channel,
      body: textToSend,
      created_at: new Date().toISOString(),
      sent_by_user_name: "you",
      _optimistic: true,
    };
    setDetail((d) => d ? { ...d, messages: [...d.messages, optimistic] } : d);
    setReplyText("");
    setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    try {
      await api.post(`/channel/conversations/${selected}/send`, { text: textToSend });
      await loadDetail(selected);
      await loadList();
    } catch (e) {
      alert("Send failed: " + (e?.response?.data?.detail || e.message));
      // rollback optimistic
      setDetail((d) => d ? { ...d, messages: d.messages.filter(m => !m._optimistic) } : d);
      setReplyText(textToSend);
    } finally { setSending(false); }
  };

  const draftReply = async () => {
    if (!selected) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/channel/conversations/${selected}/draft`, {});
      setReplyText(data.draft || "");
    } catch (e) {
      alert("Draft failed: " + (e?.response?.data?.detail || e.message));
    } finally { setDrafting(false); }
  };

  const [anchoring, setAnchoring] = useState(false);
  const [anchorResult, setAnchorResult] = useState(null);
  const anchorEvidence = async () => {
    if (!selected || !detail?.messages?.length) return;
    if (!window.confirm(`Anchor all ${detail.messages.length} messages as court-admissible evidence?\n\nThis will:\n• Compute SHA-256 hash of the canonical thread\n• Anchor it on Polygon (mainnet if configured, simulated otherwise)\n• Generate a court-ready PDF bundle with chain of custody`)) return;
    setAnchoring(true);
    setAnchorResult(null);
    try {
      const { data } = await api.post(`/channel/conversations/${selected}/anchor-evidence`, {});
      setAnchorResult(data);
    } catch (e) {
      alert("Anchor failed: " + (e?.response?.data?.detail || e.message));
    } finally { setAnchoring(false); }
  };

  const downloadCourtBundle = async (evidence_id) => {
    try {
      const { data } = await api.get(`/channel/evidence/${evidence_id}/court-bundle.pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `span_evidence_${evidence_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      alert("Download failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)] bg-[var(--surface-2)]" data-testid="channel-inbox-page">
      {/* LEFT — conversation list */}
      <div className="w-80 bg-[var(--surface)] border-r border-[var(--border)] flex flex-col">
        <div className="p-3 border-b border-[var(--border)] space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-eyebrow">Channel Manager</div>
              <h2 className="font-display font-bold text-lg">Unified Inbox</h2>
            </div>
            <button data-testid="refresh-inbox" onClick={loadList} title="Refresh" className="p-1.5 hover:bg-[var(--surface-2)] rounded">
              <ArrowsClockwise size={16} className="text-[var(--text-muted)]" />
            </button>
          </div>
          <div className="flex items-center gap-2 bg-[var(--surface-2)] rounded px-2 h-9">
            <MagnifyingGlass size={14} className="text-[var(--text-muted)]" />
            <input
              data-testid="inbox-search"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && loadList()}
              placeholder="Search name, number, message…"
              className="bg-transparent text-[12px] flex-1 outline-none"
            />
          </div>
          <div className="flex gap-1">
            {[
              ["", "All", null],
              ["whatsapp", "WA", <WhatsappLogo key="wa" size={12} weight="fill" className="text-[#25D366]" />],
              ["instagram", "IG", <InstagramLogo key="ig" size={12} className="text-[#E4405F]" />],
            ].map(([k, lbl, icon]) => (
              <button
                key={k}
                data-testid={`filter-channel-${k || "all"}`}
                onClick={() => setFilter({ ...filter, channel: k })}
                className={`flex items-center gap-1 px-2 py-1 text-[11px] rounded transition ${filter.channel === k ? "bg-[var(--brand-primary,#263776)] text-white" : "bg-[var(--surface-2)] text-[var(--text-secondary)]"}`}>
                {icon} {lbl}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {loadingList && <div className="p-4 text-[12px] text-[var(--text-muted)]">Loading…</div>}
          {!loadingList && conversations.length === 0 && (
            <div className="p-4 text-[12px] text-[var(--text-muted)]" data-testid="inbox-empty">
              No conversations yet. Inbound WhatsApp messages auto-create one.
            </div>
          )}
          {conversations.map((c) => (
            <button
              key={c.conversation_id}
              data-testid={`conv-row-${c.conversation_id}`}
              onClick={() => setSelected(c.conversation_id)}
              className={`w-full text-left p-3 border-b border-[var(--border)] hover:bg-[var(--surface-2)] transition ${selected === c.conversation_id ? "bg-[var(--surface-3)]" : ""}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  {channelIcon(c.channel)}
                  <span className="font-semibold text-[13px] truncate">{c.remote_name || c.remote_id}</span>
                </div>
                <span className="text-[10px] text-[var(--text-muted)]">{fmtTime(c.last_message_at)}</span>
              </div>
              <div className="text-[11px] text-[var(--text-muted)] font-mono truncate">{c.remote_id}</div>
              <div className="text-[12px] text-[var(--text-secondary)] truncate mt-1">{c.last_message_preview}</div>
              {c.unread_count > 0 && (
                <span data-testid={`unread-badge-${c.conversation_id}`} className="inline-block mt-1 bg-[var(--brand-accent,#F47D20)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {c.unread_count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT — thread + composer */}
      <div className="flex-1 flex flex-col">
        {!detail && (
          <div className="flex-1 flex items-center justify-center text-[var(--text-muted)] text-[13px]" data-testid="inbox-empty-state">
            <div className="text-center">
              <User size={48} className="mx-auto mb-3 opacity-30" />
              Select a conversation to view the thread.
            </div>
          </div>
        )}
        {detail && (
          <>
            {/* Header */}
            <div className="bg-[var(--surface)] border-b border-[var(--border)] px-5 py-3 flex items-center justify-between" data-testid="thread-header">
              <div className="flex items-center gap-3">
                {channelIcon(detail.conversation.channel, 20)}
                <div>
                  <div className="font-bold text-[var(--text-primary)]">{detail.conversation.remote_name}</div>
                  <div className="text-[11px] text-[var(--text-muted)] font-mono">{detail.conversation.remote_id}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {detail.lead && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase text-[var(--text-muted)]">Lead</span>
                    <span data-testid="thread-lead-status" className="badge text-[10px] font-bold uppercase">{detail.lead.status}</span>
                    {detail.lead.value_inr > 0 && (
                      <span className="text-[11px] font-mono text-[var(--brand-primary)]">₹{detail.lead.value_inr.toLocaleString("en-IN")}</span>
                    )}
                  </div>
                )}
                <button
                  data-testid="anchor-evidence-btn"
                  onClick={anchorEvidence}
                  disabled={anchoring || !detail.messages.length}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--brand-primary,#263776)] text-white text-[11px] font-semibold rounded hover:opacity-90 disabled:opacity-50 transition">
                  <Shield size={13} weight="fill" />
                  {anchoring ? "Anchoring…" : "Anchor as Evidence"}
                </button>
              </div>
            </div>

            {/* Anchor result banner */}
            {anchorResult && (
              <div data-testid="anchor-result-banner" className="bg-[var(--status-success,#16a34a)]/10 border-b border-[var(--status-success,#16a34a)]/30 px-5 py-3 flex items-center gap-3">
                <CheckCircle size={20} weight="fill" className="text-[var(--status-success)] flex-shrink-0" />
                <div className="flex-1 text-[12px]">
                  <div className="font-bold text-[var(--status-success)]">
                    {anchorResult.message_count} messages anchored · {anchorResult.chain === "polygon-mainnet" ? "Polygon Mainnet" : "Simulated (Polygon creds not configured)"}
                  </div>
                  <div className="text-[var(--text-secondary)] font-mono mt-0.5 break-all">
                    SHA-256: <span className="text-[10px]">{anchorResult.sha256_hex}</span>
                  </div>
                  <div className="text-[var(--text-secondary)] font-mono mt-0.5 break-all">
                    TX: <span className="text-[10px]">{anchorResult.tx_hash}</span>
                    {anchorResult.polygonscan_url && (
                      <a href={anchorResult.polygonscan_url} target="_blank" rel="noreferrer" data-testid="polygonscan-link" className="ml-2 text-[var(--brand-primary)] hover:underline">view on Polygonscan ↗</a>
                    )}
                  </div>
                </div>
                <button
                  data-testid="download-court-bundle"
                  onClick={() => downloadCourtBundle(anchorResult.evidence_id)}
                  className="flex items-center gap-1.5 span-btn text-[11px]">
                  <Download size={12} /> Court-Admissible PDF
                </button>
                <button data-testid="anchor-banner-close" onClick={() => setAnchorResult(null)} className="text-[var(--text-muted)] text-[16px] px-1">×</button>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-auto p-5 space-y-3 bg-[var(--surface-2)]" data-testid="messages-pane">
              {detail.messages.map((m) => (
                <div
                  key={m.message_id}
                  data-testid={`msg-${m.direction}`}
                  className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[60%] rounded-lg px-3 py-2 text-[13px] ${
                    m.direction === "outbound"
                      ? (m.channel === "instagram" ? "bg-[#E4405F] text-white" : "bg-[#25D366] text-white")
                      : "bg-[var(--surface)] text-[var(--text-primary)] border border-[var(--border)]"}`}>
                    <div className="whitespace-pre-wrap leading-snug">{m.body}</div>
                    <div className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-white/80" : "text-[var(--text-muted)]"}`}>
                      {fmtTime(m.created_at)}{m.direction === "outbound" && m.sent_by_user_name ? ` · ${m.sent_by_user_name}` : ""}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={msgEndRef} />
            </div>

            {/* Composer */}
            <div className="bg-[var(--surface)] border-t border-[var(--border)] p-3 space-y-2" data-testid="composer">
              <textarea
                data-testid="composer-textarea"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply, or click ✨ Draft to let Claude propose one…"
                rows={2}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                className="w-full span-input text-[13px]"
              />
              <div className="flex items-center justify-between">
                <button
                  data-testid="draft-reply-btn"
                  onClick={draftReply}
                  disabled={drafting}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--surface-2)] hover:bg-[var(--surface-3)] rounded text-[11px] disabled:opacity-50">
                  <Sparkle size={12} weight="fill" className="text-[var(--brand-accent,#F47D20)]" />
                  {drafting ? "Drafting…" : "Draft with Claude"}
                </button>
                <button
                  data-testid="send-message-btn"
                  onClick={send}
                  disabled={sending || !replyText.trim()}
                  className="flex items-center gap-1.5 span-btn disabled:opacity-50">
                  <PaperPlaneRight size={12} weight="fill" />
                  {sending ? "Sending…" : "Send (⌘↵)"}
                </button>
              </div>
              <div className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
                <Tag size={10} /> Outbound goes through {detail.conversation.channel === "whatsapp" ? "Twilio WhatsApp" : "Instagram Graph API"} ·
                Conversation auto-tracked in CRM (Lead status: <span className="font-bold">{detail.lead?.status || "—"}</span>)
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
